import { IMooringType, IPosition } from 'src/generated-types'
import { MapComponent } from '../localities/components/map/MapComponent'
import React, { useEffect } from 'react'
import { invalidateProjectCache, useGetProjectForMap } from 'src/api/project'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { useTranslation } from 'react-i18next'
import { Button, Stack } from '@mui/material'
import { CancelButton } from 'src/components/CancelButton'
import { ShowDialogProps } from 'src/show-dialog'
import { MapOuterStack } from 'src/components/OlexUploadConfirmDialog'
import { IConfirmOlexPositions } from 'src/generated-types/confirm-olex'
import { invalidateProjectPositionCache, useConfirmProjectPositions } from 'src/api/projectPosition'
import { toast } from 'react-toastify'
import { DialogSpinner } from 'src/components/Spinner'

interface IProjectPositionEditorMapProps extends ShowDialogProps {
  projectId: EntityId
  customerId: EntityId
  localityId: EntityId
  updatedPositions: IPosition[]
  setPositions?: React.Dispatch<React.SetStateAction<IPosition[]>>
  projectType: IMooringType
  noOfCages: number
  noOfRows: number
  noOfPens: number
  serviceRow?: boolean
  onCancel: () => void
}
const ProjectPositionMapDialog: React.FC<IProjectPositionEditorMapProps> = ({
  projectId,
  customerId,
  localityId,
  updatedPositions,
  pop,
  setPositions,
  projectType,
  noOfCages,
  noOfRows,
  noOfPens,
  serviceRow,
  onCancel,
}) => {
  const [mapPositions, setMapPositions] = React.useState<IPosition[]>([])
  const { t } = useTranslation(['work-assignment', 'common'])
  const { mutateAsync: confirmProjectPositions, isLoading } = useConfirmProjectPositions(
    customerId,
    projectId as EntityId,
  )

  const { data: project } = useGetProjectForMap(customerId as EntityId, localityId as EntityId, projectId as EntityId)

  const onConfirmPositions = async () => {
    const confirmPositionsPayload: IConfirmOlexPositions = {
      projectId: projectId,
      positions: updatedPositions,
      projectConfig: {
        type: projectType,
        noOfCages,
        noOfRows: projectType === IMooringType.LadderCage ? 1 : noOfRows,
        noOfPens,
        serviceRow,
      },
    }
    await confirmProjectPositions(confirmPositionsPayload, {
      onSuccess: data => {
        invalidateProjectPositionCache.getProjectPositions(customerId, projectId as EntityId)
        invalidateProjectCache.getProject(customerId, localityId, projectId as EntityId)
        invalidateProjectCache.getProjectMapSVG(customerId, localityId, projectId as EntityId)
        invalidateProjectCache.getProjectForMap(customerId, localityId, projectId as EntityId)
        invalidateProjectPositionCache.getProjectComponentCount(customerId, projectId as EntityId)
        toast.success(t('positions-updated-successfully', { ns: 'position' }))
        pop(true)
        setPositions?.(data.data.positions)
      },
      onError: () => {
        toast.error(t('unExpectedError', { ns: 'common-api-message' }))
      },
    })
  }

  useEffect(() => {
    if (project?.positions) {
      const projectPositions = (project.positions ?? []).map(pos => ({ ...pos, isTransparent: true }))

      setMapPositions([...projectPositions, ...updatedPositions])
    }
  }, [project, updatedPositions])

  return (
    <GenericDialogLayout
      title={t('verify-positions')}
      removedCloseButton
      pop={pop}
      actions={[
        <Stack
          className="flex justify-end gap-4 pt-4"
          key={'map-confirm-positions'}
          direction="row"
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <CancelButton
            onClick={() => {
              onCancel()
              pop(true)
            }}
          >
            {t('cancel', { ns: 'common' })}
          </CancelButton>
          <Button variant="contained" onClick={onConfirmPositions}>
            {t('verify-positions')}
          </Button>
        </Stack>,
      ]}
    >
      {isLoading && <DialogSpinner />}
      <MapOuterStack direction="column" justifyContent="space-between" alignItems="center" spacing={2}>
        <MapComponent
          positions={mapPositions as IPosition[]}
          key={'olex-map-for-project'}
          mooringType={projectType}
          layerControl={false}
          zoomControl={true}
          attributionControl={false}
          scrollWheelZoom={true}
        />
      </MapOuterStack>
    </GenericDialogLayout>
  )
}

export default ProjectPositionMapDialog
